/**
 * What's actually in today's workout.
 *
 * The queue on the Log screen is composed from three sources that disagree with
 * each other, and the composition is the whole feature:
 *
 *  - the plan's prescription, which is server-authored and must not be edited by
 *    anything that happens in one workout;
 *  - `extras`, exercises added to this session only;
 *  - `removed`, exercises taken out of this session only.
 *
 * All three used to be reasoned about inline in two different files, which is
 * how the original bug arrived: swapping one exercise for another added the new
 * one and left the old one in the queue forever, permanently unfinishable and
 * counting against the day's progress. Keeping the rules in one tested place is
 * cheaper than discovering the next disagreement mid-workout.
 *
 * The invariant worth stating: `removed` hides, it does not reclassify. A
 * prescribed exercise stays prescribed while hidden, so putting it back returns
 * it to the plan list rather than reappearing under "added today" — which would
 * quietly rewrite what the plan asked of you.
 */

/** Just enough of a PlanItem to order and identify it. */
export type QueueItem = { exercise: { id: number; name: string } | null }

export function visibleItems<T extends QueueItem>(items: T[], removed: number[]): T[] {
  if (removed.length === 0) return items
  const hidden = new Set(removed)
  // An item whose exercise the server couldn't resolve has no id to remove by,
  // so it always stays. It's part of the prescription and shows as "—".
  return items.filter((i) => !(i.exercise && hidden.has(i.exercise.id)))
}

/**
 * The "added today" list: everything worked on that the plan didn't ask for.
 *
 * Derived from sets as well as `extras` so an exercise logged straight from the
 * picker still appears — the sets are the real record and have never needed
 * permission from a plan.
 */
export function extraExerciseIds(opts: {
  extras: number[]
  setExerciseIds: number[]
  prescribedIds: Iterable<number>
  removed: number[]
}): number[] {
  const prescribed = new Set(opts.prescribedIds)
  const hidden = new Set(opts.removed)
  return [...new Set([...opts.extras, ...opts.setExerciseIds])]
    .filter((id) => !prescribed.has(id) && !hidden.has(id))
}

/** The ids a plan day prescribes, resolved exercises only. */
export function prescribedIdsOf(items: QueueItem[]): Set<number> {
  return new Set(
    items.map((i) => i.exercise?.id).filter((id): id is number => id !== undefined),
  )
}

/**
 * The next exercise to open on, ignoring anything removed.
 *
 * Landing on a removed exercise is what makes a removal not feel like one: the
 * panel opens on the very lift the list says isn't in the workout.
 */
export function firstRemaining<T extends QueueItem>(
  items: T[],
  removed: number[],
): number | null {
  return visibleItems(items, removed).find((i) => i.exercise)?.exercise?.id ?? null
}
